/**
 * @fileoverview NotificationModal Component - User notification display modal
 * 
 * Provides a comprehensive notification modal with:
 * - Real-time notification fetching
 * - Priority-based styling (High/Medium/Low)
 * - Mark as read functionality
 * - Rich HTML message support
 * - Action button navigation
 * - Sharp, no-curve design
 * 
 * @component
 * @example
 * <NotificationModal isOpen={true} onClose={() => {}} />
 * 
 * @requires React
 * @requires ../utils/apiHelper
 * @requires ./NotificationModal.css
 * 
 * @author By John Bellew - Infinia.ie - last updated 22/07/2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiHelper from '../utils/apiHelper';
import NotificationDetailModal from './NotificationDetailModal';
import './NotificationModal.css';

/**
 * NotificationModal Component
 * 
 * Renders a notification modal with live data from SharePoint.
 * 
 * Features:
 * - Fetches notifications from API
 * - Priority-based styling (High = red, Medium = orange, Low = gray)
 * - Mark individual notifications as read
 * - Mark all as read functionality
 * - Rich HTML message rendering
 * - Action button navigation
 * - Sharp, geometric design
 * 
 * @component
 * @param {Object} props Component props
 * @param {boolean} props.isOpen Whether the modal is visible
 * @param {Function} props.onClose Callback function to close the modal
 * @param {Function} props.onNotificationCountChange Callback when notification count changes
 * @param {string} props.userEmail User's email address for API calls
 * @param {boolean} props.excludeAlerts Whether to exclude Alert notifications from this modal
 * @returns {JSX.Element} NotificationModal component
 */
function NotificationModal({ isOpen, onClose, onNotificationCountChange, userEmail, excludeAlerts = false }) {
  const navigate = useNavigate();
  
  // State management
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [scrollState, setScrollState] = useState({ isScrolled: false, hasMore: false });

  // Fetch notifications when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  /**
   * Handle scroll events to show/hide scroll indicators
   */
  const handleScroll = (e) => {
    const element = e.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    const isScrolled = scrollTop > 10;
    const hasMore = scrollTop + clientHeight < scrollHeight - 10;
    
    setScrollState({ isScrolled, hasMore });
  };

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!userEmail) {
        setError('User email not available');
        return;
      }
      
      const data = await apiHelper.getNotifications(userEmail);
      
      if (data.success) {
        // Filter out Alert notifications if excludeAlerts is true
        const filteredNotifications = excludeAlerts 
          ? (data.notifications || []).filter(notification => notification.type !== 'Alert')
          : (data.notifications || []);
        
        setNotifications(filteredNotifications);
        
        // Update parent component with current count (excluding alerts if needed)
        if (onNotificationCountChange) {
          const adjustedCount = excludeAlerts 
            ? filteredNotifications.filter(n => !n.isRead).length
            : (data.unreadCount || 0);
          onNotificationCountChange(adjustedCount);
        }
      } else {
        setError('Failed to load notifications');
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Unable to load notifications. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Mark a notification as read
   * @param {string} notificationId - ID of notification to mark as read
   */
  const markAsRead = async (notificationId) => {
    try {
      await apiHelper.markNotificationRead(notificationId, userEmail);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true }
            : notification
        )
      );

      // Update parent count
      const unreadCount = notifications.filter(n => 
        n.id !== notificationId && !n.isRead
      ).length;
      
      if (onNotificationCountChange) {
        onNotificationCountChange(unreadCount);
      }
      
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  /**
   * Mark a notification as unread
   * @param {string} notificationId - ID of notification to mark as unread
   */
  const markAsUnread = async (notificationId) => {
    try {
      await apiHelper.markNotificationUnread(notificationId, userEmail);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: false }
            : notification
        )
      );

      // Update parent count - add 1 to unread count
      const currentUnreadCount = notifications.filter(n => !n.isRead).length;
      const newUnreadCount = currentUnreadCount + 1;
      
      if (onNotificationCountChange) {
        onNotificationCountChange(newUnreadCount);
      }
      
    } catch (err) {
      console.error('Error marking notification as unread:', err);
    }
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);
      
      // Mark all unread notifications as read
      await Promise.all(
        unreadNotifications.map(notification => 
          apiHelper.markNotificationRead(notification.id, userEmail)
        )
      );

      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );

      // Update parent count
      if (onNotificationCountChange) {
        onNotificationCountChange(0);
      }
      
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  /**
   * Handle notification click to open detail modal
   * @param {Object} notification - Notification to view
   */
  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setIsDetailModalOpen(true);
    
    // Auto-mark as read when clicked
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  /**
   * Close detail modal
   */
  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedNotification(null);
  };

  /**
   * Scroll to top of notifications list
   */
  const scrollToTop = () => {
    const modalBody = document.querySelector('.notification-modal-body');
    if (modalBody) {
      modalBody.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  /**
   * Handle notification action button click
   * @param {Object} notification - Notification object
   */
  const handleActionClick = (notification) => {
    // Mark as read if not already read
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    // Navigate to action URL if provided
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose(); // Close modal after navigation
    }
  };

  /**
   * Get priority-based CSS class
   * @param {string} priority - Notification priority (High/Medium/Low)
   * @returns {string} CSS class name
   */
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'High': return 'priority-high';
      case 'Medium': return 'priority-medium';
      case 'Low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  /**
   * Get notification type icon
   * @param {string} type - Notification type
   * @returns {string} FontAwesome icon class
   */
  const getTypeIcon = (type) => {
    switch (type) {
      case 'News': return 'fas fa-newspaper';
      case 'Event': return 'fas fa-calendar-alt';
      case 'Survey': return 'fas fa-poll';
      case 'Alert': return 'fas fa-exclamation-triangle';
      case 'Update': return 'fas fa-sync-alt';
      case 'Info': return 'fas fa-info-circle';
      default: return 'fas fa-bell';
    }
  };

  /**
   * Format date for display
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  /**
   * Truncate message content for display in list
   * @param {string} htmlContent - HTML message content
   * @param {number} maxLength - Maximum character length (default 500)
   * @returns {Object} { content, isTruncated }
   */
  const truncateMessage = (htmlContent, maxLength = 500) => {
    if (!htmlContent) return { content: '', isTruncated: false };
    
    // Strip HTML tags to get plain text for length calculation
    const textContent = htmlContent.replace(/<[^>]*>/g, '');
    
    if (textContent.length <= maxLength) {
      return { content: htmlContent, isTruncated: false };
    }
    
    // Find a good truncation point (try to break at word boundary)
    let truncateAt = maxLength;
    const textUpToMax = textContent.substring(0, maxLength);
    const lastSpaceIndex = textUpToMax.lastIndexOf(' ');
    
    if (lastSpaceIndex > maxLength * 0.8) { // Only use word boundary if it's not too far back
      truncateAt = lastSpaceIndex;
    }
    
    // Truncate the original HTML content more carefully
    let charCount = 0;
    let truncatedHtml = '';
    let inTag = false;
    
    for (let i = 0; i < htmlContent.length && charCount < truncateAt; i++) {
      const char = htmlContent[i];
      
      if (char === '<') {
        inTag = true;
      } else if (char === '>') {
        inTag = false;
      } else if (!inTag) {
        charCount++;
      }
      
      truncatedHtml += char;
      
      // Stop if we've reached our character limit
      if (charCount >= truncateAt) {
        break;
      }
    }
    
    // Close any open tags (basic cleanup)
    const openTags = [];
    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
    let match;
    
    while ((match = tagRegex.exec(truncatedHtml)) !== null) {
      if (match[0].startsWith('</')) {
        // Closing tag
        const tagName = match[1].toLowerCase();
        const openIndex = openTags.lastIndexOf(tagName);
        if (openIndex !== -1) {
          openTags.splice(openIndex, 1);
        }
      } else if (!match[0].endsWith('/>')) {
        // Opening tag (not self-closing)
        openTags.push(match[1].toLowerCase());
      }
    }
    
    // Close any remaining open tags
    for (let i = openTags.length - 1; i >= 0; i--) {
      truncatedHtml += `</${openTags[i]}>`;
    }
    
    return { content: truncatedHtml, isTruncated: true };
  };

  if (!isOpen) return null;

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="notification-modal-overlay" onClick={onClose}>
      <div className="notification-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="notification-modal-header">
          <h3>
            <i className="fas fa-bell"></i>
            Notifications
            {unreadCount > 0 && (
              <span className="header-unread-count">({unreadCount} unread)</span>
            )}
          </h3>
          <div className="notification-header-actions">
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={markAllAsRead}
                title="Mark all as read"
              >
                <i className="fas fa-check-double"></i>
              </button>
            )}
            <button 
              className="notification-close-btn"
              onClick={onClose}
              title="Close notifications"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div 
          className={`notification-modal-body ${scrollState.isScrolled ? 'scrolled' : ''} ${scrollState.hasMore ? 'has-more' : ''}`}
          onScroll={handleScroll}
        >
          {isLoading && (
            <div className="notification-loading">
              <i className="fas fa-spinner fa-spin"></i>
              Loading notifications...
            </div>
          )}

          {error && (
            <div className="notification-error">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          {!isLoading && !error && notifications.length === 0 && (
            <div className="notification-empty">
              <i className="fas fa-bell-slash"></i>
              <h4>No notifications</h4>
              <p>You're all caught up! New notifications will appear here.</p>
            </div>
          )}

          {!isLoading && !error && notifications.length > 0 && (
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`notification-item ${!notification.isRead ? 'unread' : 'read'} ${getPriorityClass(notification.priority)}`}
                  data-type={notification.type}
                  onClick={() => handleNotificationClick(notification)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="notification-header-item">
                    <div className="notification-icon">
                      <i className={getTypeIcon(notification.type)}></i>
                    </div>
                    <div className="notification-meta">
                      <span className="notification-type">{notification.type}</span>
                      <span className="notification-time">{formatDate(notification.created)}</span>
                    </div>
                    {!notification.isRead && (
                      <button 
                        className="mark-read-btn"
                        onClick={() => markAsRead(notification.id)}
                        title="Mark as read"
                      >
                        <i className="fas fa-check"></i>
                      </button>
                    )}
                  </div>
                  
                  <div className="notification-content">
                    <h4 className="notification-title">{notification.title}</h4>
                    {(() => {
                      const { content, isTruncated } = truncateMessage(notification.message);
                      return (
                        <div className="notification-message-wrapper">
                          <div 
                            className="notification-message"
                            dangerouslySetInnerHTML={{ __html: content }}
                          />
                          {isTruncated && (
                            <span className="message-truncated-indicator">
                              <span className="ellipsis">...</span>
                              <span className="read-more-hint">Click to read more</span>
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    
                    {notification.actionUrl && (
                      <button 
                        className="notification-action-btn"
                        onClick={() => handleActionClick(notification)}
                      >
                        <i className="fas fa-arrow-right"></i>
                        Go to page
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Scroll to Top Button */}
        {scrollState.isScrolled && (
          <button 
            className={`scroll-to-top-btn ${scrollState.isScrolled ? 'visible' : ''}`}
            onClick={scrollToTop}
            title="Scroll to top"
          >
            <i className="fas fa-chevron-up"></i>
          </button>
        )}
      </div>

      {/* Notification Detail Modal */}
      <NotificationDetailModal
        isOpen={isDetailModalOpen}
        notification={selectedNotification}
        onClose={closeDetailModal}
        onMarkAsRead={markAsRead}
        onMarkAsUnread={markAsUnread}
        userEmail={userEmail}
      />
    </div>
  );
}

export default NotificationModal; 